import { createContext } from "react";
import type { ThemeConfig, ThemesConfig } from "@/themes";

export type Theme = "light" | "dark" | "system" | "custom";

export interface RelayMetadata {
  relays: { url: string; read: boolean; write: boolean }[];
  updatedAt: number;
}

/** A custom AI provider configuration (OpenAI-compatible API). */
export interface AIProvider {
  id: string;
  name: string;
  baseUrl: string;
  apiKey: string;
  useCorsProxy: boolean;
  models: AIModel[];
  modelsFetchedAt?: number;
}

/** An AI model from a provider. */
export interface AIModel {
  id: string;
  name: string;
  providerId: string;
}

export interface AppConfig {
  appName: string;
  appId: string;
  shareOrigin?: string;
  theme: Theme;
  customTheme?: ThemeConfig;
  themes?: ThemesConfig;
  /**
   * Active Nostr relay list (NIP-65 shape). When non-empty this is the
   * exclusive connection pool; when empty the app bootstrap relays are used.
   */
  relayMetadata: RelayMetadata;
  /**
   * Blossom media servers (kind 10063 / BUD-03). Order = preference.
   * Stored locally; publish to relays when the user chooses.
   */
  blossomServers: string[];
  /**
   * Pubkey whose NIP-65 / Blossom lists were last applied into local config.
   * Used to auto-pull once per account after login without clobbering edits
   * on every reload once the user has a list for this pubkey.
   */
  networkListsPubkey?: string;
  corsProxy: string;
  aiProviders: AIProvider[];
  defaultAiProvider?: string;
  /** @deprecated kept optional so old localStorage configs still parse */
  useAppRelays?: boolean;
  /** @deprecated kept optional so old localStorage configs still parse */
  useUserRelays?: boolean;
}

export interface AppContextType {
  config: AppConfig;
  updateConfig: (
    updater: (currentConfig: Partial<AppConfig>) => Partial<AppConfig>,
  ) => void;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);
